export type Point = [number, number];

export interface ParkingGeometry {
    id?: string;
    label?: string;
    corner1?: Point;
    corner2?: Point;
    polygon_px?: Point[];
    angle?: number;
}

export function rotatePoint(
    point: Point,
    center: Point,
    degrees: number
): Point {
    const radians = degrees * Math.PI / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const dx = point[0] - center[0];
    const dy = point[1] - center[1];

    return [
        center[0] + dx * cos - dy * sin,
        center[1] + dx * sin + dy * cos
    ];
}

export function getParkingCorners(
    parking: ParkingGeometry
): Point[] {
    if (parking.polygon_px && parking.polygon_px.length >= 3) {
        return parking.polygon_px;
    }

    const c1 = parking.corner1;
    const c2 = parking.corner2;

    if (!c1 || !c2) return [];

    const minX = Math.min(c1[0], c2[0]);
    const maxX = Math.max(c1[0], c2[0]);
    const minY = Math.min(c1[1], c2[1]);
    const maxY = Math.max(c1[1], c2[1]);

    const center: Point = [
        (minX + maxX) / 2,
        (minY + maxY) / 2
    ];

    const corners: Point[] = [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY]
    ];

    const angle = Number(parking.angle || 0);

    return angle === 0
        ? corners
        : corners.map(point => rotatePoint(point, center, angle));
}

export function polygonCenter(points: Point[]): Point {
    if (!points.length) return [0, 0];

    return [
        points.reduce((sum, point) => sum + point[0], 0) / points.length,
        points.reduce((sum, point) => sum + point[1], 0) / points.length
    ];
}

export function polygonLongAxis(points: Point[]): {
    direction: Point;
    length: number;
    angle: number;
} {
    let longest = 0;
    let dx = 0;
    let dy = 1;

    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];

        const edgeX = b[0] - a[0];
        const edgeY = b[1] - a[1];
        const length = Math.hypot(edgeX, edgeY);

        if (length > longest) {
            longest = length;
            dx = edgeX;
            dy = edgeY;
        }
    }

    if (longest <= 0) {
        return {
            direction: [0, 1],
            length: 0,
            angle: 0
        };
    }

    return {
        direction: [dx / longest, dy / longest],
        length: longest,
        angle: Math.atan2(dy, dx)
    };
}
